import type {
  ConceptProjection,
  EvidenceVector,
  JourneyDefinitionV1,
  LearnerProjectionV1,
  LearningEventV1,
  MisconceptionRecord,
  ReviewItem,
} from "@seeing-maths/contracts";

export const EMPTY_EVIDENCE: EvidenceVector = {
  intuition: 0,
  manipulation: 0,
  symbolic: 0,
  explanation: 0,
  transfer: 0,
  retention: 0,
};

const clamp = (value: number) => Math.max(0, Math.min(1, value));

export function createConceptProjection(): ConceptProjection {
  return {
    visibleState: "hidden",
    stage: "notice",
    evidence: { ...EMPTY_EVIDENCE },
    representationsSucceeded: [],
    recentIndependentCorrect: 0,
    misconceptions: [],
    resumeCheckpoint: null,
  };
}

export function createLearnerProjection(learnerId: string): LearnerProjectionV1 {
  return {
    learnerId,
    projectionVersion: "1.0.0",
    revision: 0,
    asOfEventId: null,
    concepts: {},
    reviewQueue: [],
  };
}

function withEvidence(
  concept: ConceptProjection,
  dimension: keyof EvidenceVector,
  amount: number,
): ConceptProjection {
  return {
    ...concept,
    evidence: {
      ...concept.evidence,
      [dimension]: clamp(concept.evidence[dimension] + amount),
    },
  };
}

function withRepresentation(
  concept: ConceptProjection,
  representation: unknown,
): ConceptProjection {
  if (typeof representation !== "string") return concept;
  return {
    ...concept,
    representationsSucceeded: Array.from(
      new Set([...concept.representationsSucceeded, representation]),
    ),
  };
}

function upsertMisconception(
  concept: ConceptProjection,
  event: LearningEventV1,
): ConceptProjection {
  const code = String(event.payload.code ?? "UNCLASSIFIED");
  const basis = Array.isArray(event.payload.basis)
    ? event.payload.basis.map(String)
    : [event.eventId];
  const existing = concept.misconceptions.find((item) => item.code === code);
  const next: MisconceptionRecord = existing
    ? {
        ...existing,
        status: existing.basis.length + basis.length >= 2 ? "active" : "candidate",
        confidence: clamp(existing.confidence + 0.2),
        basis: Array.from(new Set([...existing.basis, ...basis])),
      }
    : { code, status: "candidate", confidence: 0.46, basis };
  return {
    ...concept,
    misconceptions: [
      ...concept.misconceptions.filter((item) => item.code !== code),
      next,
    ],
  };
}

function resolveMisconception(
  concept: ConceptProjection,
  code: unknown,
): ConceptProjection {
  if (typeof code !== "string") return concept;
  return {
    ...concept,
    misconceptions: concept.misconceptions.map((item) =>
      item.code === code ? { ...item, status: "resolved", confidence: 0.2 } : item,
    ),
  };
}

function addReview(queue: ReviewItem[], event: LearningEventV1): ReviewItem[] {
  const dueAt = String(event.payload.dueAt ?? event.occurredAt);
  const variantId = String(event.payload.variantId ?? "reversed-equation");
  const next: ReviewItem = {
    conceptId: event.conceptId,
    dueAt,
    variantId,
    reason: typeof event.payload.reason === "string" ? event.payload.reason : undefined,
  };
  return [...queue.filter((item) => item.conceptId !== event.conceptId), next];
}

export function isStrong(
  concept: ConceptProjection,
  definition?: JourneyDefinitionV1,
): boolean {
  const mastery = definition?.mastery ?? {
    symbolic: 0.72,
    explanation: 0.55,
    transfer: 0.64,
    independentCorrect: 3,
    representations: 2,
  };
  return (
    concept.evidence.symbolic >= mastery.symbolic &&
    concept.evidence.explanation >= mastery.explanation &&
    concept.evidence.transfer >= mastery.transfer &&
    concept.recentIndependentCorrect >= mastery.independentCorrect &&
    concept.representationsSucceeded.length >= mastery.representations &&
    !concept.misconceptions.some((item) => item.status === "active")
  );
}

export function projectEvent(
  projection: LearnerProjectionV1,
  event: LearningEventV1,
  definition?: JourneyDefinitionV1,
): LearnerProjectionV1 {
  let concept = projection.concepts[event.conceptId] ?? createConceptProjection();
  let reviewQueue = projection.reviewQueue;

  switch (event.type) {
    case "concept_started":
      concept = {
        ...concept,
        visibleState: concept.visibleState === "hidden" ? "discovered" : concept.visibleState,
      };
      break;
    case "stage_entered":
      if (typeof event.payload.stage === "string") {
        concept = { ...concept, stage: event.payload.stage as ConceptProjection["stage"] };
      }
      break;
    case "widget_action":
      if (event.payload.relationship === "equal" && event.payload.committed === true) {
        concept = withEvidence(concept, "manipulation", 0.6);
        concept = withRepresentation(concept, "magnitude");
      }
      break;
    case "prediction_submitted":
      if (event.payload.correct === true) {
        concept = withEvidence(concept, "intuition", 0.35);
      }
      break;
    case "answer_submitted":
      if (event.payload.correct === true && event.payload.evidenceDimension === "intuition") {
        concept = withEvidence(concept, "intuition", 0.2);
      } else if (event.payload.correct === true && event.payload.assistance !== "worked") {
        concept = withEvidence(concept, "symbolic", event.payload.assistance ? 0.18 : 0.26);
        concept = withRepresentation(concept, event.payload.representation);
        concept = {
          ...concept,
          recentIndependentCorrect:
            event.payload.assistance == null
              ? Math.min(9, concept.recentIndependentCorrect + 1)
              : concept.recentIndependentCorrect,
        };
        concept = resolveMisconception(concept, event.payload.resolves);
      }
      break;
    case "misconception_candidate":
      concept = upsertMisconception(concept, event);
      break;
    case "remediation_completed":
      concept = resolveMisconception(concept, event.payload.code);
      break;
    case "explanation_submitted":
      if (event.payload.correct === true) {
        concept = withEvidence(concept, "explanation", 0.65);
      }
      break;
    case "transfer_gate_completed":
      if (event.payload.correct === true) {
        concept = withEvidence(concept, "transfer", 0.72);
        concept = withRepresentation(concept, event.payload.representation ?? "linked-objects");
      }
      break;
    case "review_scheduled":
      reviewQueue = addReview(reviewQueue, event);
      break;
    case "review_completed":
      if (event.payload.correct === true) {
        concept = withEvidence(concept, "retention", 0.75);
        concept = { ...concept, visibleState: "remembered" };
        reviewQueue = reviewQueue.filter((item) => item.conceptId !== event.conceptId);
      }
      break;
    case "concept_state_changed":
      if (typeof event.payload.visibleState === "string") {
        concept = {
          ...concept,
          visibleState: event.payload.visibleState as ConceptProjection["visibleState"],
        };
      }
      break;
  }

  if (concept.visibleState !== "remembered") {
    concept = {
      ...concept,
      visibleState: isStrong(concept, definition)
        ? "strong"
        : concept.visibleState === "hidden"
          ? "discovered"
          : concept.visibleState,
    };
  }

  return {
    ...projection,
    revision: projection.revision + 1,
    asOfEventId: event.eventId,
    concepts: { ...projection.concepts, [event.conceptId]: concept },
    reviewQueue,
  };
}

export function projectEvents(
  projection: LearnerProjectionV1,
  events: LearningEventV1[],
  definitions: Partial<Record<string, JourneyDefinitionV1>> = {},
): LearnerProjectionV1 {
  return events.reduce(
    (state, event) => projectEvent(state, event, definitions[event.conceptId]),
    projection,
  );
}
